import { Modal } from '@mui/joy';
import React, { FC } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { AgreementType } from '@MP/shared/api/hooks/patient';
import { Agreement } from '@MP/shared/api/hooks/patient';
import { Patient } from '@MP/shared/api/hooks/patient';
import { useMinddSnackbar } from '@MP/shared/components/MinddSnackbarProvider';
import PhoneNumberUtils from '@MP/shared/utils/PhoneNumberUtils';

import { PatientEditFormProvider } from '../../providers/PatientEditFormProvider';
import usePatientAgreementRepository from '../../repositories/usePatientAgreementRepository';
import {
  createPatientEditFormDataStore,
  PatientEditFormDataStoreApi,
} from '../../stores/createPatientEditFormDataStore';
import { PatientAgreementSendViewModel } from '../../stores/PatientAgreementSendViewModel';

import { AgreementListModalDialog } from './AgreementListModalDialog';
import { PatientEditModalDialog } from './PatientEditModalDialog';
import { QRCodeModalDialog } from './QRCodeModalDialog';

interface Props {
  value: Patient;
  agreements?: Agreement[];
  onClose: () => void;
  onDelete: () => void;
}

const PatientEditDialog: FC<Props> = ({ value, agreements = [], onClose, onDelete }) => {
  const [step, setStep] = React.useState(0);
  const [formDataApi, setFormDataApi] = React.useState<PatientEditFormDataStoreApi>(() =>
    createPatientEditFormDataStore(value)
  );
  const repository = usePatientAgreementRepository();
  const [viewModelApi] = React.useState(() => PatientAgreementSendViewModel.createStore(repository));

  const { id, phoneNumber, sendPatientRegistration, sendStimHome } = useStore(
    formDataApi,
    useShallow((s) => ({
      id: s.id,
      phoneNumber: s.phoneNumber,
      sendPatientRegistration: s.sendPatientRegistration,
      sendStimHome: s.sendStimHome,
    }))
  );

  const qrUrl = useStore(viewModelApi, (s) => s.qrUrl);
  const isFetching = useStore(viewModelApi, (s) => s.isFetchingUrl);
  const isSending = useStore(viewModelApi, (s) => s.isSending);
  const fetchAgreementUrl = useStore(viewModelApi, (s) => s.fetchAgreementUrl);
  const sendAgreements = useStore(viewModelApi, (s) => s.send);

  const snackbar = useMinddSnackbar();

  const sendTypes: AgreementType[] = [];
  if (sendPatientRegistration) sendTypes.push(AgreementType.PatientRegistration);
  if (sendStimHome) sendTypes.push(AgreementType.StimHome);

  // 다른 '환자'로 바뀔 때만 폼을 새로 만든다. value(store.user)는 그 환자에 대한 실시간
  // 이벤트(설문 응답·동의 등)마다 객체 identity 가 바뀌는데, 그때마다 재생성하면 사용자가
  // 입력 중이던 값이 서버 값으로 리셋된다(특히 이 창의 동의 QR 을 그 자리서 스캔·동의 시).
  const prevIdRef = React.useRef(value.id);
  React.useEffect(() => {
    if (prevIdRef.current !== value.id) {
      prevIdRef.current = value.id;
      setFormDataApi(createPatientEditFormDataStore(value));
    }
  }, [value]);

  React.useEffect(() => {
    formDataApi.getState().setAgreementsStatus(agreements);
  }, [formDataApi, agreements]);

  const handleSendAgreements = async () => {
    if (id) {
      await fetchAgreementUrl({ patientId: id, types: sendTypes });
    }
  };

  const handleSend = async () => {
    if (id) {
      const sent = await sendAgreements({ patientId: id, types: sendTypes });
      if (sent) {
        snackbar.add('동의서가 발송되었습니다.');
        onClose();
      }
    }
  };

  return (
    <PatientEditFormProvider store={formDataApi}>
      <Modal open>
        <>
          {step === 0 && (
            <PatientEditModalDialog onClose={onClose} onNext={() => setStep(1)} onDelete={onDelete} />
          )}
          {!qrUrl && step === 1 && (
            <AgreementListModalDialog
              loading={isFetching}
              onClose={onClose}
              onPrev={() => setStep(0)}
              onSubmit={handleSendAgreements}
            />
          )}
          {qrUrl && step === 1 && (
            <QRCodeModalDialog
              url={qrUrl}
              loading={isSending}
              validPhone={PhoneNumberUtils.isValid(phoneNumber)}
              onClose={onClose}
              onSend={handleSend}
            />
          )}
        </>
      </Modal>
    </PatientEditFormProvider>
  );
};

export default PatientEditDialog;
